import React, { useContext, useState, useEffect } from 'react';
import { AddressSearchContext } from '../../App';
import { MapPin, House, Cpu, TrendingUpDown, BrainCircuit, BrainCog } from 'lucide-react';
// import Lottie from 'lottie-react';
// import loadingSpinner from '/src/assets/loading_spinner.json';
import { CreateGoogleMapsStreetviewUrl } from '/src/services/CreateGoogleMapsStreetviewUrl';
import AddressSearchProgressSecondaryConfirmation from './SecondaryConfirmation/AddressSearchProgressSecondaryConfirmation';
import { WindowSizeContext } from '/src/App';
import './style.css';

const AddressSearchProgress = () => {

    const { deviceType } = useContext(WindowSizeContext);

    const [loadingMessageIndex, setLoadingMessageIndex] = useState(0);
    const [currentLoadingMessage, setCurrentLoadingMessage] = useState('');

    const {
        addressSearchModelingTracker,
        setAddressSearchModelingTracker,
        addressSearchGoogleAutocompleteResult,
        addressSearchModelingInProgress,
        addressSearchModelingCompleted
    } = useContext(AddressSearchContext);

    // Temporary state management for progress steps
    const [progressSteps, setProgressSteps] = useState({
        addressCheck: false,
        homeConfirmation: false,
        modeling: false
    });

    const [secondaryConfirmationInfo, setSecondaryConfirmationInfo] = useState({
        propertyType: null,
        continueWithAnalysis: false
    });

    // useEffect to watch secondaryConfirmationInfo and update the addressSearchModelingTracker
    useEffect(() => {

        if (secondaryConfirmationInfo.continueWithAnalysis) {
            // console.log('secondaryConfirmationInfo', secondaryConfirmationInfo);
            setAddressSearchModelingTracker({
                ...addressSearchModelingTracker,
                processStage: 'modeling'
            });
        }
    }, [secondaryConfirmationInfo]);


    useEffect(() => {

        // console.log('addressSearchModelingTracker', addressSearchModelingTracker);
        const currentStage = addressSearchModelingTracker.processStage;

        let addressConfirmationStageStatus = 'pending';
        let homeConfirmationStageStatus = 'pending';
        let modelingStageStatus = 'pending';


        if (currentStage === 'initial') {
            addressConfirmationStageStatus = 'inProgress';
            homeConfirmationStageStatus = 'pending';
            modelingStageStatus = 'pending';
        }
        else if (currentStage === 'secondary_confirmation') {
            addressConfirmationStageStatus = 'completed';
            homeConfirmationStageStatus = 'inProgress';
            modelingStageStatus = 'pending';
        } else if (currentStage === 'modeling') {
            addressConfirmationStageStatus = 'completed';
            homeConfirmationStageStatus = 'completed';
            modelingStageStatus = 'inProgress';
        }

        setProgressSteps({
            // Address Confirmation
            addressCheck: addressConfirmationStageStatus,

            // Property Details Check
            homeConfirmation: homeConfirmationStageStatus,

            // Modeling
            modeling: modelingStageStatus
        });
    }, [addressSearchModelingTracker]);

    let streetviewUrlToUse = null;
    let mapUrlToUse = null;
    let fullAddressToUse = null;

    if (addressSearchModelingTracker.searchType === 'input') {

        if (addressSearchModelingTracker.addressDetailsGoogle) {
            const { line1, line2, streetviewUrl, mapUrl, fullAddress } = addressSearchModelingTracker.addressDetailsGoogle;
            streetviewUrlToUse = streetviewUrl;
            mapUrlToUse = mapUrl;
            fullAddressToUse = fullAddress;
        }
    } else if (addressSearchModelingTracker.searchType === 'url') {

        if (addressSearchModelingTracker.addressDetailsQuoll) {
            const { Address_Id, City, Lat, Long, Route_Long_Name, State_Short_Name, Street_Number, Zip_Code } = addressSearchModelingTracker.addressDetailsQuoll;
            streetviewUrlToUse = CreateGoogleMapsStreetviewUrl(Lat, Long, 'streetview');
            mapUrlToUse = CreateGoogleMapsStreetviewUrl(Lat, Long, 'map');
            fullAddressToUse = `${Street_Number} ${Route_Long_Name}, ${City}, ${State_Short_Name} ${Zip_Code}`;
        }

    }

    const loadingTextByStage = {
        initial: [
            'Zeroing in on the coordinates... ',
            'Finding it in the system...',
            'Getting details about the area...',
            'Looking for similar homes...'
        ],
        secondary_confirmation: [
            'Confirming property type...',
            'Is it a single family home?...',
            'May need to confirm with you...'
        ],
        modeling: [
            'Examining the home and property...',
            'Analyzing over 1K different datapoints....',
            "Making sure we cover all the bases...",
            'Testing different scenarios...',
            'Preparing resilience recommendations...',
            "Almost done..."
        ]
    }
    useEffect(() => {
        const stage = addressSearchModelingTracker.processStage;
        const messages = loadingTextByStage[stage] || [];

        if (!messages.length) {
            setCurrentLoadingMessage('');
            return;
        }

        // Reset to the first message on stage change
        setLoadingMessageIndex(0);
        setCurrentLoadingMessage(messages[0]);

        const interval = setInterval(() => {
            setLoadingMessageIndex(prev => {
                const next = (prev + 1) % messages.length;
                setCurrentLoadingMessage(messages[next]);
                return next;
            });
        }, 4000); // switch every ~1.8s

        return () => clearInterval(interval);
    }, [addressSearchModelingTracker.processStage]);


    return (
        <div className="address-progress-overlay">
            <div className="address-progress-container">
                <div className="address-progress-content">
                    <div className="address-progress-maps">
                        <div className={`address-progress-map-container box-shadow-standard ${!streetviewUrlToUse && 'loading'}`}>
                            {streetviewUrlToUse ? (
                                <img src={streetviewUrlToUse} alt="Street View" className="address-progress-map-image" />
                            ) : (
                                <div className="loading-animation-dots-bars" />
                                // <Lottie animationData={loadingSpinner} loop={true} style={{ width: 100, height: 100 }} />
                            )}
                        </div>
                        <div className={`address-progress-map-container box-shadow-standard ${!mapUrlToUse && 'loading'}`}>
                            {mapUrlToUse ? (
                                <img src={mapUrlToUse} alt="Location Map" className="address-progress-map-image" />
                            ) : (
                                <div className="loading-animation-dots-bars" />
                                // <Lottie animationData={loadingSpinner} loop={true} style={{ width: 100, height: 100 }} />
                            )}
                        </div>
                    </div>
                    {fullAddressToUse && (
                        <div className="address-progress-address">
                            <p className="address-progress-address-line text-align-center">{fullAddressToUse}</p>
                        </div>
                    )}

                    {addressSearchModelingTracker.processStage === 'secondary_confirmation' && (
                        <AddressSearchProgressSecondaryConfirmation
                            addressSearchModelingTracker={addressSearchModelingTracker}
                            secondaryConfirmationInfo={secondaryConfirmationInfo}
                            setSecondaryConfirmationInfo={setSecondaryConfirmationInfo}
                        />
                    )}

                    <div className={`address-progress-timeline ${deviceType} ${addressSearchModelingTracker.processStage === 'secondary_confirmation' ? 'secondary-confirmation' : ''}`}>
                        <div className={`address-progress-step ${progressSteps.addressCheck}`}>
                            <div className={`address-progress-step-circle`}>
                                <MapPin />
                            </div>
                            <span className="address-progress-step-text">{progressSteps.addressCheck === 'completed' ? 'Address Confirmed' : 'Finding Address'}</span>
                        </div>

                        <div className={`address-progress-line ${progressSteps.homeConfirmation}`} />

                        <div className={`address-progress-step ${progressSteps.homeConfirmation}`}>
                            <div className={`address-progress-step-circle `}>
                                <House />
                            </div>
                            <span className="address-progress-step-text">{progressSteps.homeConfirmation === 'inProgress' ? 'Checking Property Details' : progressSteps.homeConfirmation === 'completed' ? 'Property Type Confirmed' : 'Checking Property Type'}</span>
                        </div>

                        <div className={`address-progress-line ${progressSteps.modeling}`} />

                        <div className={`address-progress-step ${progressSteps.modeling}`}>
                            <div className={`address-progress-step-circle `}>
                                <TrendingUpDown />
                            </div>
                            <span className="address-progress-step-text">Analyzing Property</span>
                        </div>
                    </div>
                </div>
                <div className="address-progress-loading-text-container">
                    {(currentLoadingMessage && addressSearchModelingTracker.processStage !== 'secondary_confirmation') && (
                        <span
                            key={currentLoadingMessage}
                            className="address-progress-loading-line"
                        >
                            {currentLoadingMessage}
                        </span>
                    )}
                </div>
            </div>
        </div>
    );
};

// Simple icon components (same as provided but without TypeScript types)
const CheckIcon = () => (
    <svg className="address-progress-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <polyline points="20 6 9 17 4 12" />
    </svg>
);

const HomeIcon = () => (
    <svg className="address-progress-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="m3 9 9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
        <polyline points="9 22 9 12 15 12 15 22" />
    </svg>
);

const CalculatorIcon = () => (
    <svg className="address-progress-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <rect width="16" height="20" x="4" y="2" rx="2" />
        <line x1="8" x2="16" y1="6" y2="6" />
        <line x1="16" x2="16" y1="14" y2="18" />
        <path d="M16 10h.01" />
        <path d="M12 10h.01" />
        <path d="M8 10h.01" />
        <path d="M12 14h.01" />
        <path d="M8 14h.01" />
        <path d="M12 18h.01" />
        <path d="M8 18h.01" />
    </svg>
);

export default AddressSearchProgress;
